/**
 * Team view component — displays team info, members, and ready-up controls.
 */

import type { TeamInfo } from "@/hooks/useCommunity";
import { Shield, Users, Crown } from "lucide-react";

interface TeamViewProps {
  team: TeamInfo;
  memberCount: number;
  isOwner: boolean;
  readyMembers: Set<string>;
  isReady: boolean;
  onToggleReady: (gameType: string) => void;
  selectedGame: string;
}

export function TeamView({
  team,
  memberCount,
  isOwner,
  readyMembers,
  isReady,
  onToggleReady,
  selectedGame,
}: TeamViewProps) {
  return (
    <div className="rounded-xl border border-border bg-card">
      {/* Team header */}
      <div className="flex items-center gap-4 border-b border-border px-5 py-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-foreground">{team.name}</h2>
            {isOwner && (
              <span className="flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-500">
                <Crown className="h-3 w-3" />
                Owner
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            <Users className="mr-1 inline h-3.5 w-3.5" />
            {memberCount} member{memberCount !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Ready button */}
        {selectedGame && (
          <button
            onClick={() => onToggleReady(selectedGame)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all ${
              isReady
                ? "bg-green-500/10 text-green-500 ring-1 ring-green-500/30"
                : "bg-primary text-primary-foreground hover:opacity-90"
            }`}
          >
            {isReady ? "Ready!" : "Ready Up"}
          </button>
        )}
      </div>

      {/* Ready status bar */}
      {selectedGame && readyMembers.size > 0 && (
        <div className="border-b border-border px-5 py-3">
          <p className="text-xs font-medium text-muted-foreground">
            Ready: {readyMembers.size} of {memberCount} member{memberCount !== 1 ? "s" : ""}
          </p>
          <div className="mt-2 flex gap-1.5">
            {Array.from({ length: readyMembers.size }).map((_, i) => (
              <div
                key={i}
                className="h-2 flex-1 rounded-full bg-green-500 transition-all"
              />
            ))}
            {memberCount > readyMembers.size &&
              Array.from({ length: memberCount - readyMembers.size }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="h-2 flex-1 rounded-full bg-muted"
                />
              ))}
          </div>
        </div>
      )}

      {/* Team ID */}
      <div className="px-5 py-3">
        <p className="text-xs text-muted-foreground">
          Team ID: <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{team.id.slice(0, 16)}</code>
          {team.source !== "simple" && (
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
              Concord
            </span>
          )}
        </p>
      </div>
    </div>
  );
}